import WebOnly from '@/components/Responsive/WebOnly';
import WapOnly from '@/components/Responsive/WapOnly';
import ScanPrescriptionPC from './pc';
import ScanPrescriptionM from './m';
import { useState, useRef, useEffect } from 'react';
import ScanningDialog from '../ScanningDialog';
import ResultDialog from '../ResultDialog';
import {
  BasePrescriptionRecognition,
  IPrescriptionRecognition,
  ILoadPR,
} from '@/app/_lens-components/types';
import { EPrescriptionType } from '@/utils/enums/goods';
import { prescriptionRecognition } from '@/api/product';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { toast } from '@/components/Toast';
import logger from '@/utils/client-logger';
import { useTranslations } from 'next-intl';
import { TrackingService } from '../../services/TrackingService';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { useReporting } from '@/hooks/useReporting';
import { EDIT_PRESCRIPTION_SUB_STEPS } from '@/app/_lens-components/constants';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import { useInView } from 'react-intersection-observer';
import useTrackEditPrescription from '../../services/useTrackEditPrescription';

interface ScanPrescriptionProps {
  prescriptionType: EPrescriptionType;
  presTypeOptionName?: string;
  prLoadAnimationTime?: number;
  onLoadPrescription: (data: ILoadPR) => void;
  onFileUpload?: (fileUrl: string) => void;
  // onClickUpload?: (buttonType: 'Upload' | 'Photo') => void;
  onStartUpload?: () => void;
}

export default function ScanPrescription({
  prescriptionType,
  presTypeOptionName = '',
  prLoadAnimationTime = 3000,
  onLoadPrescription,
  onFileUpload,
  // onClickUpload,
  onStartUpload,
}: ScanPrescriptionProps) {
  const t = useTranslations('lens-process');
  const { trackEvent } = useReporting();
  const { lensGoodsItem, lensGoodsSensorsData, setEditPresStep } = useLensProcessStore();
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const { trackScanGuidePageView, trackScanGuidePageUpload } = useTrackEditPrescription();
  useEffect(() => {
    if (inView) {
      trackScanGuidePageView();
    }
  }, [inView]);
  // const [isShowPrismInfo, setIsShowPrismInfo] = useState(false);

  // 创建埋点服务
  const trackingService = new TrackingService(
    trackEvent,
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
  );

  // 弹窗相关状态
  const [isScanning, setIsScanning] = useState(false);
  const [showResultPop, setShowResultPop] = useState(false);
  const [resultData, setResultData] = useState<BasePrescriptionRecognition>(null);
  const [uploadedS3Url, setUploadedS3Url] = useState<string>('');

  // 是否取消过处方识别。取消之后，后续流程不再处理，使用Ref永远保持最新值
  const isCancelScanRef = useRef(false);

  // 开始扫描处方
  const startScan = async (fileUrl: string) => {
    try {
      setIsScanning(true);
      const response = await prescriptionRecognition({
        prescription_type: getApiPrescriptionType(prescriptionType),
        product_id: lensGoodsItem?.product_id,
        prescription_img: fileUrl,
      });

      if (isCancelScanRef.current) {
        return;
      }

      if (response.code !== 200) {
        toast.error(response.msg || t('uploadFailed'));
        setIsScanning(false);
      } else {
        setUploadedS3Url(fileUrl);
        setIsScanning(false);
        const { prescription_correction: { type } = {} } = response.data;
        setResultData(response.data);

        let prescription_scan_result_type = 'failure';

        if (type === 1) {
          // 处方填写页，识别成功
          prescription_scan_result_type = 'success';
          handleScanFinish(response.data, false, fileUrl);
        } else if ([3, 5].includes(type)) {
          // 识别失败/纠错
          setShowResultPop(true);
          prescription_scan_result_type = type === 5 ? 'correction' : 'failure';
          if (type === 5) {
            trackingService.prescriptionEnterPageScanPopupChangeExp();
          } else if (type === 3) {
            trackingService.prescriptionEnterPageScanPopupFailExp();
          }
        } else {
          toast.error(t('uploadFailed'));
          prescription_scan_result_type = 'failure';
        }

        trackingService.prescriptionEnterPageScanParamResult(prescription_scan_result_type);
      }
    } catch (error) {
      if (isCancelScanRef.current) {
        return;
      }

      setIsScanning(false);
      toast.error(t('uploadFailed'));
      logger.error('Failed to scan prescription', error);
    }
  };

  // 取消扫描
  const handleCancelScan = () => {
    setIsScanning(false);
    isCancelScanRef.current = true;
  };

  // 处理纠错弹窗确认
  const handleCorrectionConfirm = (selectedOption: { value: string; type: string }) => {
    if (selectedOption.type === 'ignoreAdd') {
      // 忽略ADD值
      handleScanFinish(resultData, true);
      trackingService.prescriptionEnterPageScanPopupChangeNextClick();
    } else if (selectedOption.type === 'changePres') {
      // 切换处方类型
      handleScanFinish({
        ...resultData,
        prescription_type: selectedOption.value,
      });
      trackingService.prescriptionEnterPageScanPopupChangeNextClick(selectedOption.value);
    }
  };

  // 处理扫描完成
  const handleScanFinish = (
    data: IPrescriptionRecognition,
    ignoreAdd = false,
    imageUrl?: string,
  ) => {
    setShowResultPop(false);
    logger.log('handleScanFinish___', data);
    const { option, prescription_type: presType, record_id } = data;
    if (onLoadPrescription && data) {
      // 选择忽略ADD，将ADD值恢复至默认
      let finalOption = { ...option };
      if (ignoreAdd) {
        finalOption = {
          ...finalOption,
          od_add: '0.00',
          os_add: '0.00',
        };
      }
      // 处理扫描结果
      const scannedOption = { ...getEmptyPrescription(), ...finalOption };

      // 优先使用传入的 imageUrl，如果没有则使用 uploadedS3Url
      const finalImageUrl = imageUrl || uploadedS3Url;
      onLoadPrescription({
        prescription: scannedOption,
        // 识别处方类型为老化时，转换为老化子类型
        presType:
          presType === EPrescriptionType.READING_GLASSES
            ? EPrescriptionType.PRESCRIPTION_READING
            : presType,
        record_id,
        prescriptions_image: finalImageUrl,
      });
      setEditPresStep(EDIT_PRESCRIPTION_SUB_STEPS.FILL_PRESCRIPTION_FORM);
    }

    trackingService.prescriptionScanSuccessPageView(presType || '');
  };

  // 处理文件上传
  const handleFileUpload = async (fileUrl: string) => {
    try {
      // 上传文件到S3
      if (fileUrl) {
        trackingService.prescriptionEnterPageUploadSuccessResult();
        isCancelScanRef.current = false;

        // 调用外部的文件上传处理
        onFileUpload?.(fileUrl);

        // 开始扫描处方
        startScan(fileUrl);
      } else {
        toast.error(t('uploadFailed'));
      }
    } catch (error) {
      toast.error(t('uploadFailed'));
      logger.error('Failed to upload prescription image', error);
    }
  };

  // 处理开始上传
  const handleStartUpload = () => {
    onStartUpload?.();
    setIsScanning(true);
  };
  return (
    <div ref={ref}>
      <WebOnly>
        <ScanPrescriptionPC
          // isShowPrismInfo={isShowPrismInfo}
          // setIsShowPrismInfo={setIsShowPrismInfo}
          onFileUpload={handleFileUpload}
          onClickUpload={trackScanGuidePageUpload}
          onStartUpload={handleStartUpload}
        />
      </WebOnly>
      <WapOnly>
        <ScanPrescriptionM
          // isShowPrismInfo={isShowPrismInfo}
          // setIsShowPrismInfo={setIsShowPrismInfo}
          onFileUpload={handleFileUpload}
          onClickUpload={trackScanGuidePageUpload}
          onStartUpload={handleStartUpload}
        />
      </WapOnly>

      {/* 扫描中弹窗 */}
      <ScanningDialog
        isOpen={isScanning}
        onCancel={handleCancelScan}
        animationTime={prLoadAnimationTime}
      />

      {/* 结果弹窗 */}
      <ResultDialog
        isOpen={showResultPop}
        onClose={() => setShowResultPop(false)}
        resultData={resultData}
        uploadedS3Url={uploadedS3Url}
        onCorrectionConfirm={handleCorrectionConfirm}
      />
    </div>
  );
}
